import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Chip,
  Stack,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
  Popover,
  Divider,
  Autocomplete,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "notistack";
import React, { useMemo, useState, useRef } from "react";
import { useQuery, useQueryClient } from "react-query";
import HeaderBreadcrumbs from "src/components/HeaderBreadcrumbs";
import Iconify from "src/components/Iconify";
import Page from "src/components/Page";
import MRTTable from "src/components/mrt-table";
import useSettings from "src/hooks/useSettings";
import useTabs from "src/hooks/useTabs";
import AddChecklist from "./AddChecklist";
import AssignChecklist from "./AssignChecklist";
import DeleteRecord from "src/components/DeleteRecord";
import QrDownloadDialog from "./QrDownloadDialog";
import PrintQrDialog from "./PrintQrDialog";
import CreateChecklist from "./CreateChecklist";
import useAuth from "src/hooks/useAuth";
import { saveAs } from "file-saver";
import {
  getAllChecklists,
  getClientsMini,
  deleteChecklist,
  getActiveCompanies,
  exportChecklists,
} from "src/pages/api";

// Columns configuration for MRTTable
function createColumns(onDownloadQr) {
  return [
    {
      accessorKey: "name",
      header: "Name",
      size: 150,
    },
    {
      accessorKey: "categoryName",
      header: "Category",
      size: 130,
    },
    {
      accessorKey: "type",
      header: "Type",
      size: 120,
    },
    {
      accessorKey: "assignedCount",
      header: "Assigned",
      size: 120,
      Cell: ({ row }) => <AssignedClientsCell row={row.original} />,
    },
    {
      accessorKey: "assignments",
      header: "Reminder",
      size: 150,
      Cell: ({ row }) => <ReminderCell row={row.original} />,
    },
    {
      accessorKey: "qr",
      header: "Download QR",
      size: 140,
      Cell: ({ row }) => (
        <QrChipCell row={row.original} onDownloadQr={onDownloadQr} />
      ),
    },
  ];
}

// - Small component: shows assigned-clients count with popover on click ----
function AssignedClientsCell({ row }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const assignments = Array.isArray(row.assignments) ? row.assignments : [];
  const count = row.assignedCount ?? assignments.length ?? 0;

  return (
    <>
      <Chip
        label={`Clients: ${count}`}
        onClick={(e) => assignments.length > 0 && setAnchorEl(e.currentTarget)}
        sx={{
          backgroundColor: "#2563EB",
          color: "#fff",
          fontWeight: 700,
          fontSize: "0.8rem",
          borderRadius: "999px",
          height: 28,
          cursor: assignments.length > 0 ? "pointer" : "default",
          "&:hover": {
            backgroundColor: assignments.length > 0 ? "#1D4ED8" : "#2563EB",
          },
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
            minWidth: 240,
          },
        }}
      >
        <Box sx={{ p: 1.5 }}>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{
              color: "#6B7280",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Assigned Clients
          </Typography>
          <Stack spacing={0} sx={{ mt: 1 }}>
            {assignments.map((a, idx) => (
              <Box key={a.id}>
                {idx > 0 && <Divider sx={{ my: 0.75 }} />}
                <Stack spacing={0.3}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: "#111827" }}
                  >
                    {a.clientName || `Client ${a.clientId}`}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {a.reminder && (
                      <Chip
                        label={a.reminder}
                        size="small"
                        sx={{
                          backgroundColor: "#EFF6FF",
                          color: "#2563EB",
                          fontWeight: 600,
                          fontSize: "0.72rem",
                          height: 22,
                          borderRadius: "6px",
                        }}
                      />
                    )}
                    {a.time && (
                      <Chip
                        label={a.time}
                        size="small"
                        sx={{
                          backgroundColor: "#F0FDF4",
                          color: "#16A34A",
                          fontWeight: 600,
                          fontSize: "0.72rem",
                          height: 22,
                          borderRadius: "6px",
                        }}
                      />
                    )}
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      </Popover>
    </>
  );
}

function ReminderCell({ row }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const assignments = Array.isArray(row.assignments)
    ? row.assignments.filter((a) => a.reminder)
    : [];

  if (assignments.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "#6B7280" }}>
        No Reminder
      </Typography>
    );
  }

  const uniqueTimes = [
    ...new Set(assignments.map((a) => a.time).filter(Boolean)),
  ];

  return (
    <>
      <Stack spacing={0.5} alignItems="center">
        {uniqueTimes.map((t, idx) => (
          <Chip
            key={idx}
            label={t}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              backgroundColor: "#2563EB",
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.8rem",
              borderRadius: "999px",
              height: 28,
              cursor: "pointer",
              "&:hover": { backgroundColor: "#1D4ED8" },
            }}
          />
        ))}
      </Stack>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
            minWidth: 220,
          },
        }}
      >
        <Box sx={{ p: 1.5 }}>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{
              color: "#6B7280",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Reminder Details
          </Typography>
          <Stack spacing={0} sx={{ mt: 1 }}>
            {assignments.map((a, idx) => (
              <Box key={a.id}>
                {idx > 0 && <Divider sx={{ my: 0.75 }} />}
                <Stack spacing={0.3}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: "#111827" }}
                  >
                    {a.clientName || `Client ${a.clientId}`}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {a.reminder && (
                      <Chip
                        label={a.reminder}
                        size="small"
                        sx={{
                          backgroundColor: "#EFF6FF",
                          color: "#2563EB",
                          fontWeight: 600,
                          fontSize: "0.72rem",
                          height: 22,
                          borderRadius: "6px",
                        }}
                      />
                    )}
                    {a.time && (
                      <Chip
                        label={a.time}
                        size="small"
                        sx={{
                          backgroundColor: "#F0FDF4",
                          color: "#16A34A",
                          fontWeight: 600,
                          fontSize: "0.72rem",
                          height: 22,
                          borderRadius: "6px",
                        }}
                      />
                    )}
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      </Popover>
    </>
  );
}

// - Small component: QR chip styled like Assigned chip
function QrChipCell({ row, onDownloadQr }) {
  return (
    <Chip
      label="Download QR"
      onClick={() => onDownloadQr?.(row)}
      sx={{
        backgroundColor: "#2563EB",
        color: "#fff",
        fontWeight: 700,
        fontSize: "0.8rem",
        borderRadius: "999px",
        height: 28,
        cursor: "pointer",
        "&:hover": { backgroundColor: "#1D4ED8" },
      }}
    />
  );
}

// - Main page 
export default function ChecklistMaster() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { themeStretch } = useSettings();
  const { user } = useAuth();
  const isCompanyUser = user?.role === "COMPANY";
  const { currentTab, onChangeTab, setCurrentTab } = useTabs("table");
  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openAssign, setOpenAssign] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openQr, setOpenQr] = useState(false);
  const [qrRow, setQrRow] = useState(null);
  const [openPrintQr, setOpenPrintQr] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [clientInputValue, setClientInputValue] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [exportLoading, setExportLoading] = useState(false);

  const tabSx = {
    borderRadius: "999px",
    fontWeight: 700,
    fontSize: "0.9rem",
    px: 3,
    py: 1,
    minHeight: 0,
    textTransform: "none",
    transition: "all 0.2s",
    "&.Mui-selected": { backgroundColor: "#2563EB", color: "#fff" },
    "&:not(.Mui-selected)": { backgroundColor: "#E5E7EB", color: "#111" },
  };

  // Debounce client search input by 400ms
  const clientSearchTimer = useRef(null);
  const handleClientInputChange = (_, value) => {
    setClientInputValue(value);
    clearTimeout(clientSearchTimer.current);
    clientSearchTimer.current = setTimeout(() => setClientSearch(value), 400);
  };

  const { data: companies = [], isLoading: companiesLoading } = useQuery({
    queryKey: ["companies-active"],
    queryFn: ({ signal }) => getActiveCompanies({ signal }),
    keepPreviousData: true,
  });

  const { data: clientsMini = [], isFetching: clientsLoading } = useQuery({
    queryKey: ["clients-mini", clientSearch, selectedCompanyId],
    queryFn: ({ signal }) =>
      getClientsMini({
        signal,
        search: clientSearch,
        limit: 50,
        companyId: selectedCompanyId,
      }),
    keepPreviousData: true,
  });

  const selectedClientId = clientId?.id ?? null;

  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      "checklists",
      pagination.pageIndex,
      pagination.pageSize,
      search,
      "",
      selectedClientId,
    ],
    queryFn: ({ queryKey, signal }) => {
      const [_k, pageIndex, pageSize, s, isActiveParam, cId] = queryKey;
      const params = {
        queryKey: [_k, pageIndex, pageSize, s, isActiveParam],
        signal,
      };
      if (cId) params.clientId = cId;
      return getAllChecklists(params);
    },
    keepPreviousData: true,
  });

  const tableData = useMemo(() => data?.data || [], [data?.data]);

  const handleDownloadQr = (row) => {
    setQrRow(row);
    setOpenQr(true);
  };

  const columns = useMemo(() => createColumns(handleDownloadQr), []);

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const blob = await exportChecklists({
        search,
        clientId: selectedClientId,
      });
      saveAs(
        new Blob([blob], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `Checklists_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
      enqueueSnackbar("Exported successfully", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err?.message || "Export failed", { variant: "error" });
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      {open && (
        <AddChecklist
          onClose={() => {
            setOpen(false);
            setSelectedRow(null);
            setIsEditMode(false);
            refetch();
          }}
          row={selectedRow}
          isEditMode={isEditMode}
        />
      )}
      {openAssign && (
        <AssignChecklist
          onClose={() => {
            setOpenAssign(false);
            refetch();
          }}
        />
      )}
      {openDelete && (
        <DeleteRecord
          row={selectedRow}
          title="Delete Checklist"
          labelKey="name"
          deleteFn={deleteChecklist}
          queryKey={["checklists"]}
          onClose={() => {
            setOpenDelete(false);
            setSelectedRow(null);
            refetch();
          }}
        />
      )}
      <QrDownloadDialog
        open={openQr}
        checklist={qrRow}
        onClose={() => {
          setOpenQr(false);
          setQrRow(null);
        }}
      />
      <PrintQrDialog open={openPrintQr} onClose={() => setOpenPrintQr(false)} />

      <Page title="Checklist Management">
        <Container maxWidth={themeStretch ? false : "xl"}>
          <HeaderBreadcrumbs
            heading="Checklists"
            links={[
              // { name: "Dashboard", href: PATH_DASHBOARD.root },
              { name: "Master" },
              { name: "Checklists" },
            ]}
          />

          <Grid container spacing={3}>
            <Grid item xs={12} sx={{ minWidth: 0, overflow: "hidden" }}>
              {/* Pill Tab Bar */}
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <Tabs
                  value={currentTab}
                  onChange={onChangeTab}
                  TabIndicatorProps={{ style: { display: "none" } }}
                  sx={{
                    backgroundColor: "#E5E7EB",
                    borderRadius: "999px",
                    p: "4px",
                    minHeight: 0,
                    "& .MuiTabs-flexContainer": { gap: "4px" },
                  }}
                >
                  <Tab value="table" label="Checklist Table" sx={tabSx} />
                  <Tab value="create" label="Create Checklist" sx={tabSx} />
                </Tabs>
              </Box>

              {currentTab === "create" ? (
                <CreateChecklist
                  onCreated={() => {
                    setCurrentTab("table");
                    refetch();
                  }}
                />
              ) : (
                <>
                  {/* Filters */}
                  <Grid container spacing={2} sx={{ mb: 2.5 }} alignItems="center">
                    <Grid item xs={12} sm={6} md={isCompanyUser ? 6 : 4}>
                      <TextField
                        placeholder="Search Checklist..."
                        fullWidth
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: "#9CA3AF", fontSize: 20 }} />
                            </InputAdornment>
                          ),
                          sx: { borderRadius: "10px", backgroundColor: "#F9FAFB" },
                        }}
                      />
                    </Grid>

                    {!isCompanyUser && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Autocomplete
                          options={companies}
                          value={companies.find((c) => c.id === selectedCompanyId) || null}
                          onChange={(_, val) => {
                            setSelectedCompanyId(val?.id ?? null);
                            setClientId(null);
                            setClientInputValue("");
                            setClientSearch("");
                            setPagination((p) => ({ ...p, pageIndex: 0 }));
                          }}
                          getOptionLabel={(opt) => opt?.name || ""}
                          isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                          loading={companiesLoading}
                          noOptionsText="No companies found"
                          fullWidth
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Filter Company"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {companiesLoading ? <CircularProgress size={16} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                                sx: { borderRadius: "10px", backgroundColor: "#F9FAFB" },
                              }}
                            />
                          )}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={isCompanyUser ? 6 : 4}>
                      <Autocomplete
                        options={clientsMini}
                        value={clientId}
                        onChange={(_, val) => {
                          setClientId(val);
                          setPagination((p) => ({ ...p, pageIndex: 0 }));
                        }}
                        inputValue={clientInputValue}
                        onInputChange={handleClientInputChange}
                        getOptionLabel={(opt) => opt?.clientName || ""}
                        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                        loading={clientsLoading}
                        disabled={!isCompanyUser && !selectedCompanyId}
                        noOptionsText="No clients found"
                        fullWidth
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Filter Client"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {clientsLoading ? <CircularProgress size={16} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                              sx: { borderRadius: "10px", backgroundColor: "#F9FAFB" },
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  {/* Action buttons */}
                  <Box sx={{ display: "flex", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
                    <Button
                      variant="contained"
                      onClick={() => setOpenAssign(true)}
                      sx={{
                        borderRadius: "12px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        px: 3,
                        py: 1,
                        backgroundColor: "#16A34A",
                        "&:hover": { backgroundColor: "#15803D" },
                        whiteSpace: "nowrap",
                      }}
                    >
                      Assign Checklist
                    </Button>

                    <Button
                      variant="contained"
                      onClick={() => setOpenPrintQr(true)}
                      startIcon={<Iconify icon="eva:printer-outline" />}
                      sx={{
                        borderRadius: "12px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        px: 3,
                        py: 1,
                        backgroundColor: "#2563EB",
                        "&:hover": { backgroundColor: "#1D4ED8" },
                        whiteSpace: "nowrap",
                      }}
                    >
                      Print QR Code
                    </Button>

                    <Tooltip title="Export Excel">
                      <span>
                        <IconButton
                          onClick={handleExportExcel}
                          disabled={exportLoading}
                        >
                          {exportLoading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Iconify
                              icon="vscode-icons:file-type-excel"
                              sx={{ width: 28, height: 28 }}
                            />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>

                  {/* MRTTable */}
                  <Box sx={{ overflowX: "auto" }}>
                    <MRTTable
                      data={tableData}
                      columns={columns}
                      manualPagination={true}
                      loading={isLoading}
                      onPaginationChange={setPagination}
                      pagination={pagination}
                      onGlobalFilterChange={(value) => {
                        setSearch(value ?? "");
                        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                      }}
                      globalFilter={search}
                      rowCount={data?.total || 0}
                      muiTableBodyRowProps={({ row }) => ({
                        onClick: () => {
                          setOpen(true);
                          setSelectedRow(row.original);
                          setIsEditMode(true);
                        },
                        sx: {
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "action.hover" },
                        },
                      })}
                      enableRowActions={true}
                      positionActionsColumn="last"
                      renderRowActions={({ row }) => (
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDelete(true);
                              setSelectedRow(row.original);
                            }}
                            color="error"
                            size="small"
                          >
                            <Iconify icon={"eva:trash-2-outline"} />
                          </IconButton>
                        </Box>
                      )}
                      enableToolbarInternalActions={false}
                      enableTopToolbar={false}
                      enableBottomToolbar={true}
                      enableColumnActions={false}
                      enableColumnFilters={false}
                      enableSorting={true}
                      enableGlobalFilter={false}
                      enableHiding={false}
                      enableDensityToggle={false}
                      enableFullScreenToggle={false}
                      enablePagination={true}
                      positionPagination="bottom"
                      enableColumnDragging={false}
                      initialState={{
                        density: "comfortable",
                        pagination: { pageSize: 10, pageIndex: 0 },
                      }}
                    />
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
        </Container>
      </Page>
    </>
  );
}
